/**
 * Types and validation schemas shared between the Cloudflare Worker API and the
 * React SPA. Environment-agnostic on purpose: no DOM, no Workers globals.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------ *
 * Difficulty (PRD §7)
 * ------------------------------------------------------------------ */

/** The six independent difficulty dimensions, with their inclusive ranges. */
export const DIFFICULTY_RANGES = {
  stranger: [1, 5],
  ask: [1, 7],
  ambiguity: [1, 4],
  rejection: [1, 4],
  status: [1, 5],
  commercial: [0, 5],
} as const satisfies Record<string, readonly [number, number]>;

export type DifficultyDimension = keyof typeof DIFFICULTY_RANGES;

export const DIFFICULTY_DIMENSIONS = Object.keys(DIFFICULTY_RANGES) as DifficultyDimension[];

export const difficultyVectorSchema = z.object({
  stranger: z.number().int(),
  ask: z.number().int(),
  ambiguity: z.number().int(),
  rejection: z.number().int(),
  status: z.number().int(),
  commercial: z.number().int(),
});

export type DifficultyVector = z.infer<typeof difficultyVectorSchema>;

/* ------------------------------------------------------------------ *
 * Enumerations
 * ------------------------------------------------------------------ */

export const hesitationBuckets = ['<5s', '5-15s', '15-60s', '1-5m', '>5m'] as const;
export type HesitationBucket = (typeof hesitationBuckets)[number];

export const expectationResults = ['easier', 'expected', 'harder'] as const;
export type ExpectationResult = (typeof expectationResults)[number];

export const assignmentStatuses = ['planned', 'started', 'completed', 'skipped'] as const;
export type AssignmentStatus = (typeof assignmentStatuses)[number];

export const goalCategories = [
  'family',
  'experience',
  'wealth',
  'business',
  'personal',
  'other',
] as const;
export type GoalCategory = (typeof goalCategories)[number];

/** Curriculum stages, PRD §8. */
export const CURRICULUM_STAGES = [1, 2, 3, 4, 5, 6] as const;
export type Stage = (typeof CURRICULUM_STAGES)[number];

/** What tomorrow's practice should emphasise, chosen by the adaptation engine. */
export const adaptationFocuses = [
  'volume',
  'initiation',
  'stranger',
  'ask',
  'ambiguity',
  'rejection',
  'status',
  'commercial',
] as const;
export type AdaptationFocus = (typeof adaptationFocuses)[number];

/* ------------------------------------------------------------------ *
 * Entities
 * ------------------------------------------------------------------ */

export interface UserProfile {
  id: string;
  name: string | null;
  timezone: string;
  dailyCallTarget: number;
  onboardingComplete: boolean;
  excludedCategories: string[];
  comfortBaseline: number | null;
  avoidedCallTypes: string | null;
  /** Where a discovery agent should look for new businesses. */
  searchLocation: string;
  createdAt: string;
  updatedAt: string;
}

export interface Goal {
  id: string;
  text: string;
  category: GoalCategory;
  importance: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CallTarget {
  id: string;
  businessName: string;
  phoneNumber: string;
  website: string | null;
  category: string;
  location: string | null;
  notes: string | null;
  timesCalled: number;
  lastCalledAt: string | null;
  active: boolean;
  source: 'manual' | 'seed' | 'discovery';
  /** Where the number was read from. Required for discovered targets. */
  sourceUrl: string | null;
  discoveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CallAssignment {
  id: string;
  date: string;
  position: number;
  targetId: string | null;
  businessName: string;
  phoneNumber: string;
  website: string | null;
  category: string;
  location: string | null;
  mission: string;
  stretchGoal: string | null;
  skillFocus: string;
  whyThisRep: string;
  difficulty: DifficultyVector;
  status: AssignmentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CallResult {
  id: string;
  assignmentId: string;
  madeCall: boolean;
  startedAt: string | null;
  completedAt: string | null;
  connected: boolean | null;
  durationSeconds: number | null;
  anxietyBefore: number;
  anxietyAfter: number;
  hesitationBucket: HesitationBucket | null;
  expectationResult: ExpectationResult | null;
  stretchCompleted: boolean | null;
  notes: string | null;
  createdAt: string;
}

export interface DailyReview {
  id: string;
  date: string;
  plannedCalls: number;
  completedCalls: number;
  observation: string;
  tomorrowStrategy: string;
  source: 'ai' | 'rules';
  generatedAt: string;
}

export interface DayPlan {
  id: string;
  date: string;
  stage: Stage;
  targetDifficulty: DifficultyVector;
  rationale: string;
  focus: AdaptationFocus | null;
  source: 'ai' | 'rules';
  createdAt: string;
}

/* ------------------------------------------------------------------ *
 * Metrics (computed in code — never by the model)
 * ------------------------------------------------------------------ */

export interface WeeklyPoint {
  weekStart: string;
  value: number;
  samples: number;
}

export interface Metrics {
  completedToday: number;
  plannedToday: number;
  completionRateToday: number | null;
  completedThisWeek: number;
  completedAllTime: number;
  currentStreak: number;
  totalCallMinutes: number | null;
  meanAnxietyBefore: number | null;
  meanAnxietyAfter: number | null;
  meanAnxietyDelta: number | null;
  anxietyBeforeByWeek: WeeklyPoint[];
  anxietyAfterByWeek: WeeklyPoint[];
  medianHesitation: HesitationBucket | null;
  hesitationDistribution: Record<HesitationBucket, number>;
  hesitationByWeek: WeeklyPoint[];
  expectationDistribution: Record<ExpectationResult, number>;
  atOrEasierThanExpectedRate: number | null;
  stretchAttempted: number;
  stretchCompleted: number;
  difficultyByWeek: { weekStart: string; mean: DifficultyVector; samples: number }[];
  /** Completion rate over the whole window, used by the adaptation engine. */
  completionRateWindow: number | null;
  daysWithHistory: number;
}

/* ------------------------------------------------------------------ *
 * API request schemas
 * ------------------------------------------------------------------ */

export const loginSchema = z.object({
  password: z.string().min(1),
});

export const onboardingSchema = z.object({
  why: z.string().min(1).max(4000),
  comfortBaseline: z.number().int().min(1).max(10).optional(),
  avoidedCallTypes: z.string().max(2000).optional(),
  dailyCallTarget: z.number().int().min(1).max(20).default(5),
  excludedCategories: z.array(z.string()).default([]),
  timezone: z.string().min(1).optional(),
  name: z.string().max(120).optional(),
});
export type OnboardingInput = z.infer<typeof onboardingSchema>;

export const profilePatchSchema = z
  .object({
    name: z.string().max(120).nullable(),
    timezone: z.string().min(1),
    dailyCallTarget: z.number().int().min(1).max(20),
    excludedCategories: z.array(z.string()),
    searchLocation: z.string().min(1).max(200),
  })
  .partial();

export const goalCreateSchema = z.object({
  text: z.string().min(1).max(500),
  category: z.enum(goalCategories).default('other'),
  importance: z.number().int().min(1).max(5).default(3),
});

export const goalPatchSchema = z
  .object({
    text: z.string().min(1).max(500),
    category: z.enum(goalCategories),
    importance: z.number().int().min(1).max(5),
    active: z.boolean(),
  })
  .partial();

export const targetCreateSchema = z.object({
  businessName: z.string().min(1).max(200),
  phoneNumber: z.string().min(7).max(32),
  website: z.string().max(500).nullable().optional(),
  category: z.string().min(1).max(60),
  location: z.string().max(200).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

export const targetPatchSchema = targetCreateSchema.partial().extend({
  active: z.boolean().optional(),
});

/**
 * A target found by a discovery agent rather than typed in by hand.
 *
 * `sourceUrl` is mandatory: it is the page the number was read from, and it is
 * the difference between a business that exists and one that sounded plausible.
 */
export const discoveredTargetSchema = targetCreateSchema.extend({
  sourceUrl: z.url().max(1000),
});

export const bulkTargetsSchema = z.object({
  targets: z.array(discoveredTargetSchema).min(1).max(100),
});
export type DiscoveredTarget = z.infer<typeof discoveredTargetSchema>;

/** What the discovery agent is told to go and find. */
export interface CategoryGap {
  category: string;
  /** Usable targets in the pool right now (active, rested, not blocked). */
  available: number;
  /** How many this category ought to have to sustain the habit. */
  wanted: number;
  needed: number;
}

export interface TargetGapsResponse {
  stage: Stage;
  searchLocation: string;
  dailyCallTarget: number;
  excludedCategories: string[];
  blockedCategories: string[];
  /** Total usable targets across every eligible category. */
  totalAvailable: number;
  /** Enough to run the habit without repeating a business for a month. */
  totalWanted: number;
  gaps: CategoryGap[];
}

/** The post-call survey. Only three fields are required (PRD §6.4). */
export const callResultSchema = z.object({
  madeCall: z.boolean(),
  anxietyBefore: z.number().int().min(1).max(10),
  anxietyAfter: z.number().int().min(1).max(10),
  hesitationBucket: z.enum(hesitationBuckets).nullable().optional(),
  expectationResult: z.enum(expectationResults).nullable().optional(),
  stretchCompleted: z.boolean().nullable().optional(),
  connected: z.boolean().nullable().optional(),
  durationSeconds: z.number().int().min(0).max(86400).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type CallResultInput = z.infer<typeof callResultSchema>;

export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const planRequestSchema = z.object({
  date: dateSchema.optional(),
  force: z.boolean().default(false),
});

export const reviewRequestSchema = z.object({
  date: dateSchema.optional(),
  force: z.boolean().default(false),
});

export const resetRequestSchema = z.object({
  confirm: z.literal('RESET'),
});

/* ------------------------------------------------------------------ *
 * API response shapes
 * ------------------------------------------------------------------ */

export interface TodayResponse {
  date: string;
  plannedCalls: number;
  completedCalls: number;
  dailyCallTarget: number;
  dayComplete: boolean;
  next: CallAssignment | null;
  remaining: CallAssignment[];
  done: CallAssignment[];
  whyConnection: string | null;
  plan: DayPlan | null;
}

export interface ReviewResponse {
  date: string;
  review: DailyReview | null;
  metrics: Metrics;
  tomorrowPlan: DayPlan | null;
}

export interface ProgressResponse {
  metrics: Metrics;
  callouts: string[];
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}
